import React, { useId } from 'react';
import { AlertTriangle, ChevronDown } from 'lucide-react';

/**
 * Κουμπιά και πεδία της νέας εμφάνισης — ΙΔΙΑ σε όλες τις σελίδες (Πίνακας, Φύλλο, συρτάρια). Ως 14/10/2026
 * ζούσαν στο printers/printingUi.tsx· μεταφέρθηκαν εδώ για να τα παίρνουν όλες οι σελίδες από ένα σημείο,
 * και το printingUi τα ξαναεξάγει (οι Εκτυπωτές δεν άλλαξαν). Μόνο tokens: ποτέ blue-600/gray-800.
 */

export const INPUT_CLS = 'w-full min-h-[44px] px-3.5 py-2.5 border border-line-strong rounded-xl bg-surface-2 text-t1 placeholder-t4 '
  + 'focus:outline-none focus:ring-2 focus:ring-ring/50 focus:border-ring text-sm disabled:opacity-50';
export const SELECT_CLS = `${INPUT_CLS} pr-8`;
export const GHOST_BTN = 'inline-flex items-center justify-center gap-1.5 min-h-[44px] px-4 rounded-xl text-sm font-medium border border-line-strong '
  + 'bg-surface-2 text-t1 hover:bg-surface-3 transition disabled:opacity-50 disabled:cursor-not-allowed';
export const PRIMARY_BTN = 'inline-flex items-center justify-center gap-1.5 min-h-[44px] px-5 rounded-xl text-sm font-semibold bg-brand text-brand-fg '
  + 'hover:brightness-110 active:scale-[0.98] transition disabled:opacity-50 disabled:cursor-not-allowed';
export const DANGER_GHOST_BTN = 'inline-flex items-center justify-center gap-1.5 min-h-[44px] px-4 rounded-xl text-sm font-medium border '
  + 'border-red-500/40 text-red-700 dark:text-red-400 hover:bg-red-500/10 transition disabled:opacity-50 disabled:cursor-not-allowed';
// Κουμπί κάτω από την εξήγηση της ενότητας (όπως «+ Διανομέας»).
export const ASIDE_BTN = 'inline-flex items-center gap-1.5 min-h-[36px] px-3 rounded-lg border border-line-strong text-t2 hover:bg-surface-3 '
  + 'text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
export const ICON_BTN = 'w-10 h-10 shrink-0 rounded-xl text-t3 hover:text-t1 hover:bg-surface-3 flex items-center justify-center '
  + 'transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

/** Πορτοκαλί σημείωση «προσοχή» (όχι σφάλμα): με εικονίδιο, κείμενο που διαβάζεται και στο σκούρο. */
export const Notice: React.FC<{ children: React.ReactNode; testId?: string; tone?: 'warn' | 'danger' }> = ({ children, testId, tone = 'warn' }) => (
  <div
    data-testid={testId}
    className={`flex items-start gap-2.5 rounded-xl border px-3.5 py-2.5 text-sm ${tone === 'danger'
      ? 'border-red-500/30 bg-red-500/10 text-red-800 dark:text-red-300'
      : 'border-amber-500/35 bg-amber-500/10 text-amber-900 dark:text-amber-200'}`}
  >
    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
    <div className="min-w-0">{children}</div>
  </div>
);

/** Κουκκίδα κατάστασης· το νόημα το λέει πάντα και το κείμενο δίπλα (όχι μόνο το χρώμα). */
export const StatusDot: React.FC<{ on: boolean }> = ({ on }) => (
  <span
    aria-hidden="true"
    className={`inline-block w-2 h-2 rounded-full shrink-0 ${on ? 'bg-emerald-500' : 'bg-red-500'}`}
  />
);

/**
 * «Προχωρημένα» που ανοίγουν με κουμπί (aria-expanded) αντί για <details>: το περιεχόμενο φτιάχνεται
 * μόνο όταν ανοίξει — π.χ. οι Επιδόσεις δεν ζητούν τίποτα από τον server αν δεν τις δεις.
 */
export const Disclosure: React.FC<{
  label: React.ReactNode;
  open: boolean;
  onToggle: (open: boolean) => void;
  children: React.ReactNode;
  testId?: string;
}> = ({ label, open, onToggle, children, testId }) => {
  const id = useId();
  return (
    <div className="rounded-xl border border-line" data-testid={testId}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => onToggle(!open)}
        className="w-full flex items-center justify-between gap-3 min-h-[44px] px-3.5 rounded-xl text-sm font-medium text-t1 hover:bg-surface-3/60 transition-colors"
      >
        <span>{label}</span>
        <ChevronDown className={`w-4 h-4 text-t3 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      {open && <div id={id} className="px-3.5 pb-4 pt-1">{children}</div>}
    </div>
  );
};
